import React, { useContext, useEffect, useState } from "react";
import {
    Grid2 as Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Box,
    IconButton,
    Radio,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup"; // For form validation
import { updateNotificationUser, updateUser } from "../Auth/auth.service";
import { ToastContainer } from "react-toastify";
import { ThemeContext } from "../../App";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useCountryParam } from "../../common/util/useCountryParam";
import { useNavigate } from "react-router-dom";

const SettingsPage: React.FC = () => {
    const { toggleTheme } = useContext(ThemeContext);
    const [darkMode, setDarkMode] = useState(false);
    const [notifications, setNotifications] = useState<any>({
        emailNotifications: false,
        smsNotifications: false,
    });
    const [showPassword, setShowPassword] = useState(false);
    const country = useCountryParam();
    const navigate = useNavigate();

    let user: any = sessionStorage.getItem('user');

    const decodePassword = (password: string) => {
        try {
            return atob(password);
        } catch (error) {
            console.error("Error decoding password:", error);
            return password;
        }
    };

    // Formik for user profile settings
    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            number: "",
            password: "",
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
            email: Yup.string().email("Invalid email format").required("Email is required"),
            number: Yup.string()
                .matches(/^\d{10}$/, "Phone number must be 10 digits")
                .required("Phone number is required"),
        }),
        onSubmit: (values) => {
            updateUser(JSON.parse(user)._id, values).then((res: any) => {
                // Handle success
            }).catch((err: any) => console.error(err));
        },
    });

    // Toggle functions for switches
    const handleThemeToggle = () => {
        setDarkMode(!darkMode);
        toggleTheme();
    };

    const handleNotificationToggle = (field: any) => {
        let tmp: any = { ...notifications };
        tmp[field] = !notifications[field];
        setNotifications(tmp);
        updateNotificationUser(JSON.parse(user)._id, notifications).then((res: any) => {
            // showSuccessToast(res.message);
            formik.setValues(res);
        }).catch((err) => console.error(err));
    };

    useEffect(() => {
        const userData = JSON.parse(user);
        const decodedPassword = decodePassword(userData.password || "");

        formik.setValues({
            ...userData,
            password: decodedPassword,
        });
    }, [user]);

    // Toggle password visibility
    const handlePasswordVisibilityToggle = () => {
        setShowPassword(!showPassword);
    };

    // Handle country selection
    const handleCountryChange = (selectedCountry: 'uk' | 'india') => {
        navigate(`/settings?country=${selectedCountry}`);
    };

    return (
        <Box padding={4}>
            <h2>Settings Page</h2>
            <Grid container spacing={4}>
                {/* User Profile Settings */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                User Profile
                            </Typography>
                            <form onSubmit={formik.handleSubmit}>
                                <TextField
                                    fullWidth
                                    name="name"
                                    placeholder="Name"
                                    variant="outlined"
                                    margin="normal"
                                    value={formik.values?.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                                <TextField
                                    fullWidth
                                    name="email"
                                    placeholder="Email"
                                    variant="outlined"
                                    margin="normal"
                                    value={formik.values?.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                                <TextField
                                    fullWidth
                                    name="number"
                                    placeholder="Phone Number"
                                    variant="outlined"
                                    margin="normal"
                                    value={formik.values?.number}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.number && Boolean(formik.errors.number)}
                                    helperText={formik.touched.number && formik.errors.number}
                                />
                                <TextField
                                    fullWidth
                                    name="password"
                                    placeholder="Password"
                                    variant="outlined"
                                    margin="normal"
                                    type={showPassword ? "text" : "password"} // Toggle between text and password type
                                    value={formik.values?.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton onClick={handlePasswordVisibilityToggle} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        ),
                                    }}
                                />
                                <Button variant="contained" color="primary" sx={{
                                    backgroundColor: "#10374A",
                                    color: "#FFFFFF",
                                    "&:hover": {
                                        backgroundColor: "#08293D",
                                    },
                                }} type="submit">
                                    Save
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Theme Settings */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Theme Settings
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={darkMode}
                                        onChange={handleThemeToggle}
                                    />
                                }
                                label="Dark Mode"
                            />
                        </CardContent>
                    </Card>
                     {/* Country Selection */}
                    <Card sx={{ mt: 2,  width: "100%"}}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Country Settings
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={country === 'uk'}
                                            onChange={() => handleCountryChange('uk')}
                                            name="ukCountry"
                                        />
                                    }
                                    label="United Kingdom"
                                />
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={country === 'india'}
                                            onChange={() => handleCountryChange('india')}
                                            name="indiaCountry"
                                        />
                                    }
                                    label="India"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Notification Settings */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Notification Preferences
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={notifications.emailNotifications}
                                        onChange={() => handleNotificationToggle('emailNotifications')}
                                        name="emailNotifications"
                                    />
                                }
                                label="Enable Email Notifications"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={notifications.smsNotifications}
                                        onChange={() => handleNotificationToggle('smsNotifications')}
                                        name="smsNotifications"
                                    />
                                }
                                label="Enable SMS Notifications"
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <ToastContainer position="top-right" autoClose={3000} />
        </Box>
    );
};

export default SettingsPage;
